// src/hooks/useNotifications.ts
import { useEffect, useMemo, useState, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';

export type AppNotification = {
  id: string;
  user_id: string;
  type?: string | null;
  title?: string | null;
  content?: string | null;
  message?: string | null;
  related_id?: string | null;
  created_at: string;
  metadata?: Record<string, any> | null;
};

export function useNotifications(limit = 20) {
  const { currentUser } = useAuth();
  const [items, setItems] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastNew, setLastNew] = useState<AppNotification | null>(null);

  const load = useCallback(async () => {
    if (!currentUser) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', currentUser.id)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (!error && data) setItems(data as AppNotification[]);
    setLoading(false);
  }, [currentUser, limit]);

  useEffect(() => {
    load();
  }, [load]);

  // Realtime: new notifications for this user
  useEffect(() => {
    if (!currentUser) return;

    const channel = supabase
      .channel('notif-' + currentUser.id) // <- no backticks
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: 'user_id=eq.' + currentUser.id, // <- no backticks
        },
        (payload) => {
          const n = payload.new as AppNotification;
          setItems((prev) => [n, ...prev].slice(0, limit));
          setLastNew(n);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUser, limit]);

  // Delete-on-read
  const markRead = useCallback(
    async (id: string) => {
      if (!currentUser) return;
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', id)
        .eq('user_id', currentUser.id);
      if (!error) setItems((prev) => prev.filter((n) => n.id !== id));
    },
    [currentUser]
  );

  const markAllRead = useCallback(
    async () => {
      if (!currentUser || items.length === 0) return;
      const ids = items.map((n) => n.id);
      const { error } = await supabase
        .from('notifications')
        .delete()
        .in('id', ids)
        .eq('user_id', currentUser.id);
      if (!error) setItems([]);
    },
    [currentUser, items]
  );

  const unreadCount = useMemo(() => items.length, [items]);

  return {
    items,
    unreadCount,
    loading,
    lastNew,
    clearLastNew: () => setLastNew(null),
    markRead,
    markAllRead,
    reload: load,
  };
}